import axios, { AxiosInstance, AxiosResponse } from 'axios'
import { useAuthStore } from '@/stores/auth'

class ApiClient {
  private instance: AxiosInstance

  constructor() {
    this.instance = axios.create({
      baseURL: (import.meta.env.VITE_API_BASE_URL as string) || 'https://api.openapps.ai/api/v1',
      timeout: 120000,
      headers: {
        'Content-Type': 'application/json',
      },
    })

    this.setupInterceptors()
  }

  private setupInterceptors() {
    // Request interceptor to add auth token (prefer Clerk session token if available)
    this.instance.interceptors.request.use(
      async (config) => {
        // Use Clerk browser SDK via global if available
        try {
          const clerk: any = (window as any).Clerk
          const clerkToken = clerk?.session ? await clerk.session.getToken().catch(() => undefined) : undefined
          if (clerkToken) {
            config.headers.Authorization = `Bearer ${clerkToken}`
            return config
          }
        } catch (_) {
          // ignore and fall back to local token store
        }

        const token = useAuthStore.getState().token
        if (token) {
          config.headers.Authorization = `Bearer ${token}`
        }
        return config
      },
      (error) => {
        return Promise.reject(error)
      }
    )

    // Response interceptor to handle auth errors
    this.instance.interceptors.response.use(
      (response) => {
        // Some endpoints may incorrectly return HTTP 200 with an auth error message in the body.
        // Detect common cases and force a logout + redirect to login.
        try {
          const data: any = response?.data
          let messageText = ''
          if (typeof data === 'string') {
            messageText = data
          } else if (data && typeof data === 'object') {
            const candidates = [data.message, data.error, data.detail, data.status, data.reason]
            for (const c of candidates) {
              if (typeof c === 'string') {
                messageText = c
                break
              }
            }
          }

          const text = (messageText || '').toString().toLowerCase().trim()
          const indicators = [
            'invalid authorization token',
            'authorization token required',
            'missing or invalid authorization token',
            'invalid token',
            'token validation failed',
            'token expired',
            'invalid token audience',
            'invalid issuer',
            'user not found',
          ]
          if (indicators.some((s) => text.includes(s))) {
            useAuthStore.getState().clearAuth()
            window.location.href = '/auth/login'
            return Promise.reject(new Error(messageText || 'Authentication required'))
          }

          // Some APIs may return a boolean success field with a 200
          if (data && typeof data === 'object' && data.success === false) {
            useAuthStore.getState().clearAuth()
            window.location.href = '/auth/login'
            return Promise.reject(new Error('Authentication required'))
          }
        } catch (_) {
          // no-op
        }
        return response
      },
      (error) => {
        if (error.response?.status === 401) {
          useAuthStore.getState().clearAuth()
          window.location.href = '/auth/login'
        }
        return Promise.reject(error)
      }
    )
  }

  // Generic request methods
  async get<T>(url: string, params?: any): Promise<T> {
    const response: AxiosResponse<T> = await this.instance.get(url, { params })
    return response.data
  }

  async post<T>(url: string, data?: any): Promise<T> {
    const response: AxiosResponse<T> = await this.instance.post(url, data)
    return response.data
  }

  async put<T>(url: string, data?: any): Promise<T> {
    const response: AxiosResponse<T> = await this.instance.put(url, data)
    return response.data
  }

  async delete<T>(url: string): Promise<T> {
    const response: AxiosResponse<T> = await this.instance.delete(url)
    return response.data
  }
}

export const api = new ApiClient()

// Type definitions for API responses
export interface MCPServer {
  id: string
  name: string
  version: string
  port: number
  transport: string
  status: 'running' | 'stopped' | 'error'
  created_at: string
  updated_at: string
  tools_count: number
  auth_supported: boolean
  cache_enabled: boolean
  owner_id: string
  team_id?: string
}

export interface ServerUsageStats {
  total_requests: number
  requests_today: number
  avg_response_time: number
  error_rate: number
  last_request: string
  cache_hit_rate?: number
}

export interface ServerLog {
  id: string
  server_id: string
  timestamp: string
  level: 'info' | 'warn' | 'error' | 'debug'
  message: string
  metadata?: Record<string, any>
}

export interface Template {
  id: string
  name: string
  description: string
  author: string
  version: string
  tags: string[]
  category: string
  openapi_spec: any
  config: any
  created_at: string
  updated_at: string
}

export interface Team {
  id: string
  name: string
  description: string
  owner_id: string
  members: TeamMember[]
  created_at: string
  updated_at: string
}

export interface TeamMember {
  user_id: string
  user_name: string
  user_email: string
  role: 'owner' | 'admin' | 'member'
  joined_at: string
}

export interface CreateServerRequest {
  openapi_spec: any
  server_name: string
  server_id?: string
  transport?: string
  authentication?: any
  cache?: any
  team_id?: string
}

export interface AuthCredential {
  scheme_name: string
  credentials: {
    type: string
    value: string
    username?: string
    scopes?: string[]
  }
  expires_at?: string
}

export interface SecurityScheme {
  type: string
  scheme?: string
  bearer_format?: string
  in?: string
  name?: string
  description?: string
}

export interface SecuritySchemeRequest {
  type: string
  scheme?: string
  bearer_format?: string
  in?: string
  name?: string
  description?: string
}

export interface SecuritySchemeResponse {
  message: string
  scheme_name: string
  type: string
  created_at?: string
  updated_at?: string
}

export interface ConnectionConfig {
  server_name: string
  server_id: string
  transport: string
  endpoint: string
  base_url: string
  mcp_endpoint: string
  auth_schemes: Record<string, any>
  default_auth: string[]
  port: number
  status: string
}

export interface OAuth2AuthorizationConfig {
  enabled: boolean
  authorization_server: {
    issuer: string
    authorization_endpoint: string
    token_endpoint: string
    introspection_endpoint: string
    revocation_endpoint: string
    jwks_uri: string
    registration_endpoint: string
    scopes_supported: string[]
    response_types_supported: string[]
    grant_types_supported: string[]
    code_challenge_methods_supported: string[]
    token_endpoint_auth_methods_supported: string[]
  }
  resource_metadata: {
    resource: string
    authorization_servers: string[]
    scopes_supported: string[]
    bearer_methods_supported: string[]
  }
  dynamic_registration: {
    enabled: boolean
    initial_access_token_required: boolean
    registration_endpoint: string
  }
  registered_clients: Array<{
    client_id: string
    client_name: string
    client_uri?: string
    redirect_uris: string[]
    grant_types: string[]
    response_types: string[]
    scopes: string[]
    created_at: string
    status: 'active' | 'disabled' | 'revoked'
  }>
  // TTL fields as string duration format (e.g., "1h", "30m", "24h")
  access_token_ttl?: string
  refresh_token_ttl?: string
  authorization_code_ttl?: string
}

export interface OAuth2ClientRequest {
  client_name: string
  client_uri?: string
  redirect_uris: string[]
  grant_types: string[]
  response_types: string[]
  scopes: string[]
}

export interface OAuth2Client {
  client_id: string
  client_name: string
  client_secret?: string
  client_uri?: string
  redirect_uris: string[]
  grant_types: string[]
  response_types: string[]
  scopes: string[]
  created_at: string
  status: 'active' | 'disabled' | 'revoked'
}

// API service functions
export const authApi = {
  login: (email: string, password: string) =>
    api.post<{ user: any; token: string }>('/auth/login', { email, password }),
  
  register: (name: string, email: string, password: string) =>
    api.post<{ user: any; token: string }>('/auth/register', { name, email, password }),
  
  getCurrentUser: () =>
    api.get<{ user: any }>('/auth/me'),
}

// System monitoring API
export interface SystemMonitoringResponse {
  system: {
    cpu_percent: number
    memory_used: number
    memory_total: number
    memory_percent: number
    last_updated: string
  }
  servers: Record<string, {
    server_id: string
    process_id: number
    cpu_percent: number
    memory_mb: number
    memory_percent: number
    status: string
    create_time: number
    last_updated: string
  }>
  summary: {
    total_servers_monitored: number
    user_id: string
    server_count: number
  }
  host_info?: {
    hostname: string
    platform: string
    platform_family: string
    uptime: number
  }
  generated_at: string
}

export const monitoringApi = {
  getSystemMetrics: () =>
    api.get<SystemMonitoringResponse>('/monitoring/system'),
}

export const serversApi = {
  list: () =>
    api.get<{ servers: MCPServer[]; count: number }>('/servers'),
  
  get: (id: string) =>
    api.get<MCPServer>(`/servers/${id}`),
  
  getMetrics: (id: string) =>
    api.get<{ server_id: string; metrics: any; retrieved_at: string; user_id?: string }>(`/servers/${id}/metrics`),
  
  create: (data: CreateServerRequest) =>
    api.post<MCPServer>('/generate', data),
  
  delete: (id: string) =>
    api.delete(`/servers/${id}`),
  
  getUsageStats: (id: string) =>
    api.get<ServerUsageStats>(`/servers/${id}/usage/stats`),

  // Usage logs for a specific server
  getUsageLogs: (
    id: string,
    params?: {
      start_time?: string
      end_time?: string
      limit?: number
      offset?: number
      tool_name?: string
      errors_only?: boolean
      transport?: string
    }
  ) => api.get<UsageLogsResponse>(`/servers/${id}/logs/usage`, params),

  // Existing generic logs (legacy)
  getLogs: (id: string, params?: { limit?: number; level?: string; since?: string }) =>
    api.get<{ logs: ServerLog[]; total: number }>(`/servers/${id}/logs`, params),
  
  getCacheStats: (id: string) =>
    api.get<any>(`/servers/${id}/cache/stats`),
  
  clearCache: (id: string) =>
    api.post(`/servers/${id}/cache/clear`),
  
  setCredentials: (id: string, data: AuthCredential) =>
    api.post(`/servers/${id}/auth/credentials`, data),
  
  getCredentials: (id: string) =>
    api.get<{ credentials: string[] }>(`/servers/${id}/auth/credentials`),
  
  deleteCredentials: (id: string, schemeName: string) =>
    api.delete(`/servers/${id}/auth/credentials?scheme_name=${schemeName}`),
  
  getAuthInfo: (id: string) =>
    api.get(`/servers/${id}/auth`),
  
  getConnectionConfig: (id: string) =>
    api.get<ConnectionConfig>(`/servers/${id}/connection-config`),

  // Resources management
  listResources: (id: string) =>
    api.get<{ server_id: string; resources: any[]; count: number }>(`/servers/${id}/resources`),
  addResource: (id: string, resource: { uri: string; name: string; description?: string; mime_type?: string; text?: string }) =>
    api.post<{ message: string; server_id: string; resource: any }>(`/servers/${id}/resources`, { resource }),
  removeResource: (id: string, uri: string) =>
    api.delete<{ message: string; server_id: string; uri: string; removed: boolean }>(`/servers/${id}/resources?uri=${encodeURIComponent(uri)}`),

  // Prompts management
  listPrompts: (id: string) =>
    api.get<{ server_id: string; prompts: any[]; count: number }>(`/servers/${id}/prompts`),
  addPrompt: (id: string, prompt: { name: string; description?: string; arguments?: Record<string, any>; instructions?: string }) =>
    api.post<{ message: string; server_id: string; prompt: any }>(`/servers/${id}/prompts`, { prompt }),
  removePrompt: (id: string, name: string) =>
    api.delete<{ message: string; server_id: string; name: string; removed: boolean }>(`/servers/${id}/prompts?name=${encodeURIComponent(name)}`),
  
  updateToolDescription: (id: string, toolName: string, description: string) =>
    api.put(`/servers/${id}/tools/${toolName}`, { description }),
  
  // Security scheme management
  listSecuritySchemes: (id: string) =>
    api.get<{ server_id: string; security_schemes: Record<string, SecurityScheme>; count: number }>(`/servers/${id}/auth/schemes`),
  
  createSecurityScheme: (id: string, schemeName: string, data: SecuritySchemeRequest) =>
    api.post<SecuritySchemeResponse>(`/servers/${id}/auth/schemes?scheme_name=${schemeName}`, data),
  
  getSecurityScheme: (id: string, schemeName: string) =>
    api.get<{ server_id: string; scheme_name: string; scheme: SecurityScheme }>(`/servers/${id}/auth/schemes/${schemeName}`),
  
  updateSecurityScheme: (id: string, schemeName: string, data: SecuritySchemeRequest) =>
    api.put<SecuritySchemeResponse>(`/servers/${id}/auth/schemes/${schemeName}`, data),
  
  deleteSecurityScheme: (id: string, schemeName: string) =>
    api.delete<{ message: string; server_id: string; scheme_name: string }>(`/servers/${id}/auth/schemes/${schemeName}`),

  // Tool management
  enableTool: (id: string, toolName: string) =>
    api.post<{ server_id: string; tool_name: string; enabled: boolean; message: string; timestamp: string }>(`/servers/${id}/tools/${toolName}/enable`),
  
  disableTool: (id: string, toolName: string) =>
    api.post<{ server_id: string; tool_name: string; enabled: boolean; message: string; timestamp: string }>(`/servers/${id}/tools/${toolName}/disable`),

  // OAuth 2.1 authorization server management
  getOauthConfig: (id: string) =>
    api.get<OAuth2AuthorizationConfig>(`/servers/${id}/oauth/config`),
  
  updateOauthConfig: (id: string, config: OAuth2AuthorizationConfig) =>
    api.put<{ message: string; config: OAuth2AuthorizationConfig }>(`/servers/${id}/oauth/config`, config),
  
  registerOauthClient: (id: string, clientData: OAuth2ClientRequest) =>
    api.post<OAuth2Client>(`/servers/${id}/oauth/clients`, clientData),
  
  getOauthClients: (id: string) =>
    api.get<{ clients: OAuth2Client[]; count: number }>(`/servers/${id}/oauth/clients`),
  
  getOauthClient: (id: string, clientId: string) =>
    api.get<OAuth2Client>(`/servers/${id}/oauth/clients/${clientId}`),

  // MCP Configuration for server
  getMCPConfig: (id: string) =>
    api.get<MCPConfigResponse | { message: string; server_id: string; config: null }>(`/servers/${id}/mcp-config`),
  
  updateOauthClientStatus: (id: string, clientId: string, status: 'active' | 'disabled' | 'revoked') =>
    api.put<{ message: string; client: OAuth2Client }>(`/servers/${id}/oauth/clients/${clientId}/status`, { status }),
  
  deleteOauthClient: (id: string, clientId: string) =>
    api.delete<{ message: string; client_id: string }>(`/servers/${id}/oauth/clients/${clientId}`),
  
  getOauthServerMetadata: (id: string) =>
    api.get<any>(`/servers/${id}/.well-known/oauth-authorization-server`),
  
  getOauthResourceMetadata: (id: string) =>
    api.get<any>(`/servers/${id}/.well-known/oauth-protected-resource`),
}

export const templatesApi = {
  list: (params?: { category?: string; tag?: string }) =>
    api.get<{ templates: Template[]; count: number }>('/templates', params),
  
  get: (id: string) =>
    api.get<Template>(`/templates/${id}`),
  
  create: (template: Partial<Template>) =>
    api.post<{ template_id: string; message: string }>('/templates', { template }),
  
  update: (id: string, template: Partial<Template>) =>
    api.put<{ message: string }>(`/templates/${id}`, { template }),
  
  delete: (id: string) =>
    api.delete(`/templates/${id}`),
  
  apply: (data: { template_id: string; server_name: string; parameters?: any; overrides?: any }) =>
    api.post<MCPServer>('/templates/apply', data),
  
  getCategories: () =>
    api.get<{ categories: string[]; count: number }>('/templates/categories'),
  
  getTags: () =>
    api.get<{ tags: string[]; count: number }>('/templates/tags'),
}

export const workspacesApi = {
  list: () =>
    api.get<{ workspaces: any[]; count: number }>('/workspaces'),
  
  get: (id: string) =>
    api.get<any>(`/workspaces/${id}`),
  
  create: (data: { name: string; description?: string; settings?: any; limits?: any }) =>
    api.post<any>('/workspaces', data),
  
  update: (id: string, data: { name?: string; description?: string; settings?: any; limits?: any }) =>
    api.put<any>(`/workspaces/${id}`, data),
  
  delete: (id: string) =>
    api.delete(`/workspaces/${id}`),
  
  inviteMember: (id: string, data: { email: string; role: string; permissions?: string[]; message?: string }) =>
    api.post(`/workspaces/${id}/members`, data),
  
  updateMember: (id: string, memberId: string, data: { role?: string; permissions?: string[]; is_active?: boolean }) =>
    api.put(`/workspaces/${id}/members/${memberId}`, data),
  
  removeMember: (id: string, memberId: string) =>
    api.delete(`/workspaces/${id}/members/${memberId}`),
  
  acceptInvitation: (invitationId: string) =>
    api.post(`/invitations/${invitationId}/accept`, {}),
}

// MCP Configuration Types
export interface MCPRemoteConfig {
  type: 'mcp'
  server_label: string
  server_url: string
  headers?: Record<string, string>
  allowed_tools?: string[]
  require_approval: 'always' | 'never' | 'auto'
  authentication?: MCPAuthentication
  timeout?: string
  retry_policy?: MCPRetryPolicy
  rate_limiting?: MCPRateLimiting
  cache_config?: MCPCacheConfig
  transport?: MCPTransportConfig
  security?: MCPSecurityConfig
  monitoring?: MCPMonitoringConfig
  metadata?: Record<string, any>
}

export interface MCPAuthentication {
  type: 'api_key' | 'bearer' | 'oauth2' | 'basic' | 'custom_scheme'
  api_key?: {
    key: string
    header?: string
    prefix?: string
    location?: 'header' | 'query' | 'cookie'
    name?: string
  }
  bearer?: {
    token: string
    format?: string
  }
  oauth2?: {
    access_token: string
    refresh_token?: string
    client_id?: string
    client_secret?: string
    token_url?: string
    authorization_url?: string
    scopes?: string[]
    redirect_uri?: string
  }
  basic?: {
    username: string
    password: string
  }
  custom_scheme?: {
    scheme: string
    credentials?: Record<string, string>
    headers?: Record<string, string>
  }
}

export interface MCPRetryPolicy {
  max_retries: number
  initial_delay?: string
  max_delay?: string
  backoff_factor?: number
  retry_on?: number[]
  exclude_on?: number[]
}

export interface MCPRateLimiting {
  requests_per_second?: number
  requests_per_minute?: number
  requests_per_hour?: number
  burst_size?: number
  window_size?: string
}

export interface MCPCacheConfig {
  enabled: boolean
  ttl?: string
  max_size?: number
  strategy?: 'lru' | 'lfu' | 'fifo' | 'random'
  key_pattern?: string
  invalidation?: {
    on_write?: boolean
    on_status_codes?: number[]
    on_headers?: string[]
    manual_keys?: string[]
  }
}

export interface MCPTransportConfig {
  type?: 'http' | 'https' | 'sse' | 'websocket' | 'stdio'
  max_connections?: number
  keep_alive?: string
  idle_timeout?: string
  max_request_size?: number
  max_response_size?: number
  compression_level?: number
  enable_compression?: boolean
}

export interface MCPSecurityConfig {
  tls_verify?: boolean
  allowed_hosts?: string[]
  forbidden_headers?: string[]
  required_headers?: string[]
  sanitize_input?: boolean
  validate_response_schema?: boolean
  max_redirects?: number
}

export interface MCPMonitoringConfig {
  enabled?: boolean
  log_level?: 'debug' | 'info' | 'warn' | 'error'
  log_requests?: boolean
  log_responses?: boolean
  metrics_enabled?: boolean
  tracing_enabled?: boolean
  health_check?: {
    enabled?: boolean
    interval?: string
    timeout?: string
    endpoint?: string
    threshold?: number
  }
  alerting?: {
    enabled?: boolean
    error_threshold?: number
    latency_threshold?: string
    success_rate_threshold?: number
    channels?: Array<{
      type: string
      endpoint?: string
      config?: Record<string, string>
    }>
  }
}

export interface MCPConfigRequest {
  config: MCPRemoteConfig
}

export interface MCPConfigResponse {
  server_label: string
  config: MCPRemoteConfig
  message: string
}

export interface MCPConfigListResponse {
  configs: Record<string, MCPRemoteConfig>
  count: number
}

export interface MCPConfigValidationResponse {
  valid: boolean
  errors?: string[]
  config?: MCPRemoteConfig
}

export interface MCPTemplateRequest {
  server_url: string
  auth_type?: 'api_key' | 'bearer' | 'oauth2' | 'basic' | 'custom_scheme'
}

export interface MCPTemplateResponse {
  template: MCPRemoteConfig
  message: string
}

// MCP Configuration API methods
export const mcpConfigsApi = {
  list: () =>
    api.get<MCPConfigListResponse>('/mcp-configs'),
  
  get: (label: string) =>
    api.get<MCPConfigResponse>(`/mcp-configs/${label}`),
  
  create: (data: MCPConfigRequest) =>
    api.post<MCPConfigResponse>('/mcp-configs', data),
  
  update: (label: string, data: MCPConfigRequest) =>
    api.put<MCPConfigResponse>(`/mcp-configs/${label}`, data),
  
  delete: (label: string) =>
    api.delete<{ message: string; server_label: string }>(`/mcp-configs/${label}`),
  
  validate: (data: MCPConfigRequest) =>
    api.post<MCPConfigValidationResponse>('/mcp-configs/validate', data),
  
  generateTemplate: (data: MCPTemplateRequest) =>
    api.post<MCPTemplateResponse>('/mcp-configs/template', data),
  
  importFromOpenAI: (data: any) =>
    api.post<MCPConfigResponse>('/mcp-configs/import/openai', data),
  
  exportToOpenAI: (label: string) =>
    api.get<{ openai_format: any; server_label: string; message: string }>(`/mcp-configs/${label}/export/openai`),
  
  getSchema: () =>
    api.get<any>('/mcp-configs/schema'),
}

export const teamsApi = {
  list: () =>
    api.get<{ teams: Team[]; count: number }>('/teams'),
  
  get: (id: string) =>
    api.get<Team>(`/teams/${id}`),
  
  create: (data: { name: string; description: string }) =>
    api.post<Team>('/teams', data),
  
  update: (id: string, data: { name?: string; description?: string }) =>
    api.put<Team>(`/teams/${id}`, data),
  
  delete: (id: string) =>
    api.delete(`/teams/${id}`),
  
  addMember: (id: string, data: { user_email: string; role: string }) =>
    api.post(`/teams/${id}/members`, data),
  
  updateMemberRole: (id: string, userId: string, role: string) =>
    api.put(`/teams/${id}/members/${userId}`, { role }),
  
  removeMember: (id: string, userId: string) =>
    api.delete(`/teams/${id}/members/${userId}`),
}

// Cache API types
export interface CacheStats {
  cache_enabled: boolean
  hits: number
  misses: number
  hit_rate: string
  size: number
  max_size: number
  memory_usage: string
  evictions: number
  last_cleanup: string | null
  average_latency: string
  default_ttl: string
  max_ttl: string
}

export interface CacheStatsResponse {
  server_id: string
  cache_enabled: boolean
  stats: CacheStats
  message?: string
  server_info: {
    name: string
    transport: string
    status: string
    created_at: string
  }
}

export interface CacheInvalidateRequest {
  pattern?: string
  all?: boolean
}

export interface CacheInvalidateResponse {
  server_id: string
  invalidated_count: number
  pattern?: string
  all?: boolean
  message: string
}

// Cache API methods
export const cacheApi = {
  getStats: (serverId: string) =>
    api.get<CacheStatsResponse>(`/servers/${serverId}/cache/stats`),
  
  clear: (serverId: string) =>
    api.post<{ server_id: string; message: string }>(`/servers/${serverId}/cache/clear`),
  
  invalidate: (serverId: string, data: CacheInvalidateRequest) =>
    api.post<CacheInvalidateResponse>(`/servers/${serverId}/cache/invalidate`, data),
} 

export interface UsageLogEntry {
  timestamp: string
  server_id: string
  server_name?: string
  request_id?: string
  user_id?: string
  team_id?: string
  method: string
  tool_name?: string
  transport?: string
  parameters?: Record<string, any>
  response_time?: number
  status_code?: number
  error_message?: string
  request_size?: number
  response_size?: number
  cache_hit?: boolean
  auth_method?: string
  client_ip?: string
  user_agent?: string
  tags?: Record<string, string>
}

export interface UsageLogsResponse {
  entries: UsageLogEntry[]
  total_count: number
}

// Analytics API (if needed for global logs)
export const analyticsApi = {
  getDashboard: (timeRange: string) =>
    api.get<any>('/analytics/dashboard', { time_range: timeRange }),
  
  getTopTools: () =>
    api.get<{ top_tools: Array<{ tool_name: string; usage_count: number }>; generated_at: string; user_id: string; server_count: number }>(
      '/analytics/top-tools'
    ),
  
  getActivity: (params?: { limit?: number }) =>
    api.get<any>('/analytics/activity', params),
  
  getUsageLogs: (params?: {
    start_time?: string
    end_time?: string
    limit?: number
    offset?: number
    tool_name?: string
    errors_only?: boolean
    transport?: string
    cache_hit?: boolean
    min_response_time?: number
    status_code?: number
  }) => api.get<UsageLogsResponse>('/logs/usage', params),
}
